"use client";

/**
 * NicheV3DetailClient — thin client wrapper for v3 Detail scrolltell.
 *
 * Rendered by the `@detail/c/[candidateId]` Parallel Routes slot. Wires
 * NicheDetailV2 handlers:
 *   - onBack             → push /niche/{sessionId} (drops the `/c/…` segment
 *                          → `@grid/page.tsx` resumes its active state and the
 *                          foil badge morphs back home via layoutId)
 *   - onDive(otherId)    → push /niche/{sessionId}/c/{otherId}  (swap sibling
 *                          without leaving the LayoutGroup context)
 */

import { useRouter } from "next/navigation";
import { useCallback, useEffect, useMemo } from "react";
import { NicheDetailV2 } from "./NicheDetailV2";
import { ScrollResetOnMount } from "@/components/shared/ScrollResetOnMount";
import type { NicheCandidateV3, SessionMetaV3 } from "@/types/niche-v3";
import type { ParsedDebate } from "@/lib/debate-types";
import type { ViewerRole } from "@/lib/artifact-viewer-role";
import { urls } from "@/lib/urls";
import { buildSlug } from "@/lib/slug";

interface NicheV3DetailClientProps {
  sessionId: string;
  candidateId: string;
  niche: NicheCandidateV3;
  debate: ParsedDebate | null;
  sessionMeta: SessionMetaV3;
  credits?: number;
  isLoggedIn?: boolean;
  /** Session created_at / updated_at — share-card cache-bust. */
  updatedAt?: string;
  planArtifact?: {
    id: string;
    status: "generating" | "pending" | "complete" | "failed";
  } | null;
  /** Owner flag — drives VisibilityToggle render in the top bar. */
  isOwner?: boolean;
  initialIsPublic?: boolean;
  tierAllowsPrivate?: boolean;
  /** Viewer-role classifier — drives the bottom action block in DecideScene. */
  viewerRole?: ViewerRole;
}

export function NicheV3DetailClient({
  sessionId,
  candidateId,
  niche,
  debate,
  sessionMeta,
  credits,
  isLoggedIn,
  updatedAt,
  planArtifact,
  isOwner,
  initialIsPublic,
  tierAllowsPrivate,
  viewerRole,
}: NicheV3DetailClientProps) {
  const router = useRouter();
  // Niche row for URL builder — slug derived from current niche name; falls back
  // to bare id if name is empty (page route self-heals to canonical).
  const nicheRow = useMemo(
    () => ({ id: sessionId, slug: buildSlug(niche.name, sessionId) }),
    [sessionId, niche.name],
  );
  const survivorsHref = urls.niche(nicheRow);

  useEffect(() => {
    router.prefetch(survivorsHref);
  }, [router, survivorsHref]);

  const handleBack = useCallback(() => {
    // Drop the `/c/<candidateId>` segment → the grid slot resumes its active
    // state and the LayoutGroup reverses the foil-badge morph. We intentionally
    // don't carry `selected` so the transition owns the "you came from here"
    // feel visually.
    router.push(survivorsHref);
  }, [router, survivorsHref]);

  const handleDive = useCallback(
    (otherCandidateId: string) => {
      // Sibling switch — swap the `/c/<candidateId>` segment in place. This
      // keeps the same layout tree so the LayoutGroup context (and any in-flight
      // morph) is preserved across the URL change.
      router.push(urls.nicheCandidate(nicheRow, otherCandidateId));
    },
    [router, nicheRow],
  );

  return (
    <>
      <ScrollResetOnMount />
      <NicheDetailV2
      sessionId={sessionId}
      candidateId={candidateId}
      niche={niche}
      debate={debate}
      sessionMeta={sessionMeta}
      onBack={handleBack}
      onDive={handleDive}
      credits={credits}
      isLoggedIn={isLoggedIn}
      updatedAt={updatedAt}
      planArtifact={planArtifact}
      isOwner={isOwner}
      initialIsPublic={initialIsPublic}
      tierAllowsPrivate={tierAllowsPrivate}
      viewerRole={viewerRole}
    />
    </>
  );
}
